import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { captureException } from '@sentry/angular';
import { TranslateService } from '@ngx-translate/core';
import { InterceptorSkipHeader } from '../auth.interceptor';
import { ERRORS } from '../common/utils/notification-constants';
import { AuthTracing } from '../utilities/auth-tracing.utils';
import { ActionsQueryParamKeys } from '../common/utils/actions-query-params-util';
import { URL_CONSTANTS } from '../common/utils/url';
import { AuthService } from './auth.service';
import { URLService } from './dynamic-url.service';
import { NotificationToasterService } from './notification-toaster.service';
import { TelemetryService } from './telemetry.service';

const THIRD_PARTY_SSO_ATTEMPTED_EVENT = 'third_party_sso_attempted';

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyLoginService {
  constructor(
    @Optional() private readonly auth: Auth,
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService,
    private readonly telemetry: TelemetryService,
    private readonly notificationsService: NotificationToasterService,
    private readonly router: Router,
    private readonly urlService: URLService,
    private readonly translate: TranslateService,
  ) {}

  async isTokenMatchingCurrentUser(token: string): Promise<boolean> {
    // Wait for Firebase Auth initialization to complete before proceeding
    // This is important for route guards that run early during app startup,
    // when the auth state may not yet be available (e.g., currentUser is null).

    await this.authService.loading;

    // Get current user
    const currentUser = this.auth?.currentUser;

    // Parse packed token format (token~renewCode) if present
    const { rawToken } = this.authService.parsePackedToken(token);

    // Decode token
    const decodedToken = this.authService.decodeToken(rawToken);

    return decodedToken?.uid === currentUser?.uid;
  }

  async signOutForAPILogin() {
    AuthTracing.log('ThirdPartyLoginService.signOutForAPILogin');
    await this.auth?.signOut();
  }

  async loginWithCustomToken(token: string) {
    return await signInWithCustomToken(this.auth, token);
  }

  buildSpaceContinueUrl(
    spaceId: string,
    queryParams?: { [K in ActionsQueryParamKeys]?: string },
  ): string {
    const queryString = queryParams
      ? new URLSearchParams(queryParams as Record<string, string>).toString()
      : '';
    const qs = queryString ? `?${queryString}` : '';
    return `${URL_CONSTANTS.SPACES}/${spaceId}${qs}`;
  }

  async renewSpectatorToken(renewCode: string): Promise<string | null> {
    const response = await firstValueFrom(
      this.httpClient.post<{ token: string }>(
        `${this.urlService.getDynamicUrl()}/spectator/renew-token`,
        { renewCode },
        {
          headers: {
            [InterceptorSkipHeader]: 'true',
          },
        },
      ),
    );
    return response?.token ?? null;
  }

  public displayCustomLoginErrorToasterAndRoute(
    message: string,
    title: string,
    errorCode?: string,
  ) {
    if (errorCode) {
      this.telemetry.log('error', `${errorCode}: API user sign in failed`);
    }
    this.notificationsService.showDefaultErrorNotificationV2(
      title,
      message,
      ERRORS.AUTH_FAILED,
      undefined,
      undefined,
      5,
      true,
    );
    return this.router.navigateByUrl(
      `/account/login?continueUrl=${this.authService.getRedirectUrl()}`,
    );
  }

  /** FullStory + Sentry + error toast (no navigation). */
  reportThirdPartySignInFailure(message: string, error: unknown): void {
    this.telemetry.event(THIRD_PARTY_SSO_ATTEMPTED_EVENT, { error });

    captureException(error, {
      extra: { message },
    });

    const title = this.translate.instant('Sign in failed');
    this.notificationsService.showDefaultErrorNotificationV2(
      title,
      message,
      ERRORS.AUTH_FAILED,
      undefined,
      undefined,
      5,
      true,
    );
  }
}
